/**
 * Format a date string for short display (e.g., "Jan 15")
 */
export function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Format a timestamp as relative time or short date.
 * - < 1 min: "Just now"
 * - < 60 min: "Xm ago"
 * - < 24 hours: "Xh ago"
 * - < 7 days: "Xd ago"
 * - else: "Jan 15"
 */
export function formatRelativeTime(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/**
 * Format a timestamp for display with date and time.
 * e.g., "Jan 15, 2025, 3:30 PM"
 */
export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

/**
 * Format a YYYY-MM month string for display.
 * e.g., "2025-01" -> "Jan '25"
 */
export function formatMonth(monthStr: string): string {
  const [year, month] = monthStr.split('-')
  const date = new Date(parseInt(year), parseInt(month) - 1)
  return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

/**
 * Format a week range for display.
 * e.g., "Jan 13 - 19"
 */
export function formatWeekRange(start: string, end: string): string {
  const startDate = new Date(start)
  const endDate = new Date(end)
  return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { day: 'numeric' })}`
}

/**
 * Format a date for user-friendly display.
 * e.g., "Jan 15, 2025"
 */
export function formatDateFull(dateString: string): string {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

/**
 * Format runtime in minutes to human-readable format.
 * - > 36 hours: "X.X Days"
 * - else: "Xh Ym"
 */
export function formatRuntime(minutes: number): string {
  const hours = minutes / 60
  if (hours > 36) {
    const days = hours / 24
    return `${days.toFixed(1)} Days`
  }
  const wholeHours = Math.floor(hours)
  const mins = Math.round(minutes % 60)
  if (wholeHours === 0) return `${mins}m`
  if (mins === 0) return `${wholeHours}h`
  return `${wholeHours}h ${mins}m`
}

/**
 * Format a date for chart display.
 * e.g., "Jan 15"
 */
export function formatChartDate(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * Format a time for chart tooltips.
 * e.g., "Jan 15, 3:30 PM"
 */
export function formatChartTime(timestamp: string): string {
  const date = new Date(timestamp)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Check if a timestamp is stale (older than threshold).
 */
export function isStale(timestamp: string | undefined, thresholdMinutes: number): boolean {
  if (!timestamp) return false
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = diffMs / 60000
  return diffMins > thresholdMinutes
}

/**
 * Format seconds to human-readable duration.
 * e.g., "1h 30m", "45m", "2h 0m"
 */
export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  if (hours === 0) return `${mins}m`
  if (mins === 0) return `${hours}h`
  return `${hours}h ${mins}m`
}

/**
 * Format age for stale data display.
 * e.g., "2m ago", "1h ago", "3d ago"
 */
export function formatAge(timestamp: string): string {
  const date = new Date(timestamp)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  return `${diffDays}d ago`
}
